/**
 * qaRouteMap
 *
 * Maps a QA checklist `slug` to an in-app route a platform admin can visit to
 * exercise the surface ("Go verify"), plus an optional one-line hint on how to
 * verify it. Surfaces with no single page (edge functions, integrations,
 * pipelines) are intentionally route-less; the Dev Mode hub falls back to the
 * checklist's `verifyHint` frontmatter and then to a per-kind generic hint.
 *
 * Routes are the operator-app paths defined in apps/web/src/App.tsx +
 * each module's `basePath` (see apps/web/src/shell/moduleRegistry.ts).
 */
export interface QaRouteMapping {
  /** In-app path to deep-link to, e.g. '/clients'. Undefined => no page. */
  route?: string;
  /** Short "how to verify" hint shown when there's no route (or alongside). */
  hint?: string;
}

export const QA_ROUTE_MAP: Record<string, QaRouteMapping> = {
  // ---- modules (page-backed) ----
  calendar: { route: '/calendar', hint: 'Create an appointment, then confirm reminders fire.' },
  clients: { route: '/clients', hint: 'Create a client, edit it, open the inbox.' },
  contracts: { route: '/contracts', hint: 'Open a contract template version and attach it to a proposal.' },
  fulfillment: { route: '/fulfillment', hint: 'Walk a ship-type order through the hub queue + packing station.' },
  'lot-management': { route: '/lots', hint: 'Create a lot, attach a COA, print a 2x2 label.' },
  marketing: { route: '/marketing', hint: 'Draft a bulk SMS campaign and check recipient resolution.' },
  orders: { route: '/orders', hint: 'Draft a proposal, send it, approve via the public link, invoice it.' },
  products: { route: '/products', hint: 'Create a product, set per-store entitlement + pricing.' },
  reports: { route: '/reports', hint: 'Open P&L and drill into a channel breakdown.' },
  rewards: { route: '/rewards', hint: 'Inspect a client wallet + referral ledger.' },
  shipping: { route: '/shipping', hint: 'Connect a carrier, quote a rate, buy/refund a test label.' },
  shop: { route: '/shop', hint: 'Browse vendors, follow one, open a message thread.' },
  vendors: { route: '/vendors', hint: 'Create a supplier with W9 status and SKU links.' },
  wholesalers: { route: '/wholesalers', hint: 'Create a wholesale account with terms + tier pricing.' },

  // ---- cross-cutting / settings (page-backed where a single screen exists) ----
  'auth-and-mfa': {
    route: '/settings/security',
    hint: 'Toggle MFA here, then sign out and back in to exercise the 2FA challenge.',
  },
  'billing-gate-and-quotas': {
    route: '/settings/billing',
    hint: 'Check plan, quotas, and the trial/lockout gate from the billing screen.',
  },
  'team-and-roles': {
    route: '/settings/team',
    hint: 'Invite a teammate, change a role, confirm the writer/viewer gate.',
  },
  'email-and-sms-templates': {
    route: '/settings/templates',
    hint: 'Edit an email/SMS template and send a test.',
  },
  'pdf-templates': {
    route: '/settings/pdfs',
    hint: 'Open a PDF template and render a sample document.',
  },
  'integrations-and-connectors': {
    route: '/settings/developer/integrations',
    hint: 'Review connected integrations + connectors.',
  },
  'api-keys': {
    route: '/settings/developer/api-keys',
    hint: 'Mint and revoke an API key.',
  },
  webhooks: {
    route: '/settings/developer/webhooks',
    hint: 'Register a webhook and send a test delivery.',
  },
  'bulk-import': {
    route: '/settings/import',
    hint: 'Run a bulk client import from the settings screen.',
  },
  'usage-metering': {
    route: '/settings/usage',
    hint: 'Confirm usage counters match live data.',
  },
};

/**
 * Build the reverse map (route -> slug) once. Used by the Dock to detect which
 * surface the current page maps to. Longest route wins on prefix ties so
 * `/clients/inbox` resolves to `clients` only when no more specific slug exists.
 */
const ROUTE_TO_SLUG: Array<{ route: string; slug: string }> = Object.entries(QA_ROUTE_MAP)
  .filter(([, m]) => typeof m.route === 'string' && m.route)
  .map(([slug, m]) => ({ route: m.route as string, slug }))
  .sort((a, b) => b.route.length - a.route.length);

/**
 * Resolve the QA surface slug for a given pathname, or null. Matches the most
 * specific (longest) mapped route that is a path-segment prefix of `pathname`.
 */
export function slugForPathname(pathname: string): string | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  for (const { route, slug } of ROUTE_TO_SLUG) {
    if (path === route || path.startsWith(`${route}/`)) return slug;
  }
  return null;
}
